import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { FormProvider, useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Card, CircularProgress, Stack, Typography, IconButton } from "@mui/material"
import { toast } from "react-toastify"
import { FormBlock } from "../../../../../common/FormGenerator"
import { getDynamicConfig } from "./settingsConfig"
import { whatAvailableSchema } from "../ZodValidationSchema/WhatAvailableSchema"
import { useGetProjectWhatAvailableQuery, useUpdateProjectWhatAvailableMutation } from "../../../../../api/projects"
import { CardStyle } from "./style"
import { IconCloseRed } from "../../../../../icons/IconCloseRed"

export const EditWhatAvailableForm = () => {
    const { id } = useParams<{ id: string }>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const projectId = id ? parseInt(id, 10) : NaN
    const {
        data: loaded,
        isLoading,
        isError,
    } = useGetProjectWhatAvailableQuery(
        { projectId },
        { skip: Number.isNaN(projectId) }
    )
    const [updateWhatAvailable] = useUpdateProjectWhatAvailableMutation()

    const methods = useForm({
        mode: 'onBlur',
        resolver: zodResolver(whatAvailableSchema),
        defaultValues: {
            whatAvailable: [{ title: "", description: "" }],
        },
    })

    const { control, handleSubmit, reset } = methods
    const { fields, append, remove } = useFieldArray({
        control,
        name: "whatAvailable",
    })

    useEffect(() => {
        if (loaded) {
            reset(loaded)
        }
    }, [loaded, reset])

    useEffect(() => {
        if (isError) {
            toast.error("Не удалось загрузить данные что уже есть")
        }
    }, [isError])

    const addItem = () => {
        append({
            title: "",
            description: "",
        })
    }

    const onSubmit = async (data: Record<string, unknown>) => {
        if (Number.isNaN(projectId)) return

        setIsSubmitting(true)
        try {
            await updateWhatAvailable({ projectId, body: data }).unwrap()
            toast.success("Данные 'Что уже есть' сохранены")
        } catch {
            toast.error("Не удалось сохранить данные 'Что уже есть'")
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress />
            </Stack>
        )
    }

    return (
        <FormProvider {...methods}>
            <Stack gap={2} component="form" onSubmit={handleSubmit(onSubmit)} height="100%">
                {fields.length === 0 ? (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '60vh' }}
                    >
                        <Typography variant="black-16-semiBold">
                            Список "что уже есть" пуст
                        </Typography>
                    </Stack>
                ) : (
                    fields.map((field, index) => (
                        <Card key={field.id} variant="primaryCard" sx={CardStyle}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography
                                    variant="black-16-semiBold"
                                    component="div"
                                >
                                    Название
                                </Typography>
                                <IconButton
                                    onClick={() => remove(index)}
                                    size="small"
                                    sx={{ p: 0, my: -0.5, }}
                                >
                                    <IconCloseRed fontSize="small" />
                                </IconButton>
                            </Stack>
                            <Box mb={1.5}>
                                <FormBlock formFieldConfig={getDynamicConfig('titleWhatAvailable', index, "whatAvailable")} />
                            </Box>
                            <Typography
                                variant="black-16-semiBold"
                                component="div"
                                mb={1}
                            >
                                Описание
                            </Typography>
                            <FormBlock formFieldConfig={getDynamicConfig('descriptionWhatAvailable', index, "whatAvailable")} />
                        </Card>
                    ))
                )}
                <Stack direction="row" spacing={1}>
                    <Button
                        type="button"
                        variant="action"
                        onClick={addItem}
                    >
                        Добавить
                    </Button>

                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="follow"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CircularProgress size={16} color="inherit" />
                                    <span>Сохранение...</span>
                                </Stack>
                            ) : (
                                "Сохранить"
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </FormProvider>
    )
}